// useAnalytics — reusable hook over the analytics service (Firebase GA4).
//
// Usage:
//   const { trackEvent, trackPageView, trackImpression, observeImpression } = useAnalytics();
//   trackEvent('add_to_cart', { item_id, price, qty });
//   <div ref={observeImpression({ item_id: dish.id, item_name: dish.name, list_name: 'menu' })} />
//
// Mount ONCE near the app root with { trackPageViews: true } to auto-fire page_view on
// every SPA route change. Everywhere else, just call the returned helpers.

import { useCallback, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
    trackEvent as svcTrackEvent,
    trackPageView as svcTrackPageView,
    trackImpression as svcTrackImpression,
    setAnalyticsUser,
    clearAnalyticsUser,
    EVENTS,
} from '../utils/analytics';

export { EVENTS };

export function useAnalytics({ trackPageViews = false } = {}) {
    const { user } = useAuth();
    const location = useLocation();

    // Keep the analytics user (id/role + masked phone/email) in sync with auth state.
    useEffect(() => {
        if (user?.id) setAnalyticsUser(user);
        else clearAnalyticsUser();
    }, [user?.id]); // eslint-disable-line react-hooks/exhaustive-deps

    // Optional auto page-view tracking (enable on the root instance only).
    useEffect(() => {
        if (!trackPageViews) return;
        svcTrackPageView(location.pathname + location.search);
    }, [trackPageViews, location.pathname, location.search]);

    const trackEvent = useCallback((name, params, opts) => svcTrackEvent(name, params, opts), []);
    const trackPageView = useCallback((path, params) => svcTrackPageView(path, params), []);
    const trackImpression = useCallback((items, opts) => svcTrackImpression(items, opts), []);

    // Shared IntersectionObserver for catalogue impressions.
    const observerRef = useRef(null);
    const payloadMap = useRef(new WeakMap());

    useEffect(() => {
        if (typeof IntersectionObserver === 'undefined') return undefined;
        const io = new IntersectionObserver((entries) => {
            entries.forEach((en) => {
                if (en.isIntersecting && en.intersectionRatio >= 0.5) {
                    const payload = payloadMap.current.get(en.target);
                    if (payload) {
                        svcTrackImpression([payload], { list_name: payload.list_name });
                        io.unobserve(en.target); // once per element
                    }
                }
            });
        }, { threshold: [0, 0.5, 1] });
        observerRef.current = io;
        return () => io.disconnect();
    }, []);

    // Returns a ref callback to attach to each catalogue card.
    const observeImpression = useCallback((payload) => (node) => {
        const io = observerRef.current;
        if (!io) return;
        if (node) { payloadMap.current.set(node, payload); io.observe(node); }
    }, []);

    return { trackEvent, trackPageView, trackImpression, observeImpression };
}

export default useAnalytics;
